package org.moxultimate.topwithmox.shows.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import org.moxultimate.topwithmox.shows.data.ShowsResponse
import org.moxultimate.topwithmox.shows.data.ShowsTagResponse
import org.moxultimate.topwithmox.shows.data.ShowsTypeResponse
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState

/**
 * ShowsRepository
 * Handles all read operations for shows, tags, and types.
 * Write operations (create, update, delete, saveTag, saveType) live in ShowsCRUDRepository.
 *
 * @param httpClient Ktor HttpClient injected via Koin.
 */
class ShowsRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/shows"
    private val tagUrl  = "$baseAPI/shows-tag"
    private val typeUrl = "$baseAPI/shows-type"

    /**
     * getShows
     * Fetches a page of shows using cursor-based pagination (20 items, newest first).
     * Pass [beforeId] = Long.MAX_VALUE to load the first page.
     *
     * @param beforeId Pagination cursor — loads items with id strictly less than this value.
     * @return Flow emitting Loading → Success(List<ShowsResponse>) or Error.
     */
    fun getShows(beforeId: Long = Long.MAX_VALUE): Flow<APIState<List<ShowsResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get(baseUrl) {
                parameter("beforeId", beforeId)
            }
        })
    }

    /**
     * getShowById
     * Fetches a single show entry via GET /shows/{id}.
     *
     * @param id Show ID to fetch.
     * @return Flow emitting Loading → Success(ShowsResponse) or Error.
     */
    fun getShowById(id: Long): Flow<APIState<ShowsResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.get("$baseUrl/$id") })
    }

    /**
     * getAllTags
     * Fetches all show tags from GET /shows-tag.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTags(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<ShowsTagResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(tagUrl) },
                transformer = { entities -> entities.mapNotNull { it.tag }.distinct() },
            )
        )
    }

    /**
     * getAllTypes
     * Fetches all show types from GET /shows-type. Used to populate the type filter chips.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTypes(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<ShowsTypeResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(typeUrl) },
                transformer = { entities -> entities.mapNotNull { it.type }.distinct() },
            )
        )
    }
}
